import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Loader2, MapPin, ShieldQuestion } from "lucide-react";
import { ricordaVisita } from "@/components/VisitaOspite";

/**
 * L'ingresso del visitatore.
 *
 * Due passi. Prima il codice che il preposto ha letto in cantiere: si controlla
 * subito, cosi' chi lo ha copiato male se ne accorge prima di scrivere i propri
 * dati e non dopo. Poi chi e': nome, cognome, ruolo, azienda.
 *
 * Il codice vale una volta sola e per pochi minuti. Non e' una password da
 * tenere: e' un gettone da spendere sul posto.
 */
export default function PaginaVisita() {
  const navigate = useNavigate();
  // Da dove si arriva: il QR di una persona o di un mezzo manda qui e dice
  // dove tornare. Si accettano solo indirizzi interni, mai un sito di fuori.
  const [parametri] = useSearchParams();
  const grezzo = parametri.get("vai") || "";
  const dovePoi = grezzo.startsWith("/") && !grezzo.startsWith("//") ? grezzo : "";
  const [codice, setCodice] = useState("");
  const [buono, setBuono] = useState(null);
  const [verifico, setVerifico] = useState(false);
  const [entro, setEntro] = useState(false);
  const [f, setF] = useState({ nome: "", cognome: "", ruolo: "", azienda: "",
                               email: "", telefono: "", motivo: "" });

  const verifica = async () => {
    setVerifico(true);
    try {
      const { data } = await api.get(`/visite/codice/${encodeURIComponent(codice.trim())}`);
      setBuono(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Codice non riconosciuto");
    } finally { setVerifico(false); }
  };

  const manca = !f.nome.trim() || !f.cognome.trim() || !f.ruolo.trim()
                || !f.azienda.trim();

  const entra = async () => {
    if (manca) { toast.error("Nome, cognome, ruolo e azienda sono obbligatori"); return; }
    setEntro(true);
    try {
      const { data } = await api.post(
        `/visite/codice/${encodeURIComponent(codice.trim())}/entra`, f);
      if (data.token) localStorage.setItem("sgm_token", data.token);
      // Va scritta qui, non piu' tardi: la pagina del QR su cui si atterra
      // registra la tappa appena si apre, e senza questo il primo QR del giro
      // non verrebbe contato.
      ricordaVisita(data);
      toast.success(`Benvenuto ${data.nome}`);
      // Si torna al QR che si stava leggendo; se si e' arrivati dal login,
      // si atterra sulla pagina della visita, che ha lo scan in grande e i
      // documenti da consultare: e' da li' che parte il giro.
      window.location.href = dovePoi || "/visita/documenti";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
      setEntro(false);
    }
  };

  const campo = (chiave, etichetta, extra = {}) => (
    <div>
      <Label>{etichetta}{extra.obbligatorio && <span className="text-orange-500"> *</span>}</Label>
      <Input value={f[chiave]} className="h-11 mt-1"
             data-testid={`visita-${chiave}`}
             autoComplete={extra.autoComplete}
             onChange={(e) => setF({ ...f, [chiave]: e.target.value })} />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="pagina-visita">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#0067b2] text-white flex items-center justify-center mx-auto mb-3">
            <ShieldQuestion className="w-7 h-7" />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-1">
            SGM Impianti
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter">
            Visita in cantiere
          </h1>
        </div>

        {!buono ? (
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
            <p className="text-sm text-slate-600">
              {dovePoi
                ? "Questo QR è riservato a chi lavora in cantiere e a chi è in visita. "
                  + "Chiedete il codice al preposto: vale una volta sola e per pochi minuti."
                : "Chiedete il codice al preposto del cantiere. Vale una volta sola e per "
                  + "pochi minuti."}
            </p>
            <div>
              <Label>Codice di visita</Label>
              <Input value={codice} data-testid="campo-codice"
                     onChange={(e) => setCodice(e.target.value.toUpperCase())}
                     onKeyDown={(e) => { if (e.key === "Enter") verifica(); }}
                     placeholder="ABC-123" autoCapitalize="characters"
                     autoComplete="off" spellCheck="false"
                     className="h-14 mt-1 text-center font-mono text-2xl tracking-[0.3em]" />
            </div>
            <Button onClick={verifica} disabled={verifico || codice.trim().length < 6}
                    data-testid="verifica-codice"
                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white">
              {verifico ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        : <KeyRound className="w-4 h-4 mr-2" />}
              Continua
            </Button>
            <button type="button" onClick={() => navigate("/login")}
                    className="w-full text-xs text-slate-500 hover:text-slate-800 py-1">
              Ho un account SGM
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-2">
              <div className="text-xs uppercase tracking-widest text-emerald-800">
                Codice valido
              </div>
              <div className="font-semibold flex items-center gap-1.5 mt-0.5">
                <MapPin className="w-4 h-4 text-emerald-700" /> {buono.cantiere_nome}
              </div>
              <div className="text-xs text-slate-600">
                Autorizzato da {buono.generato_da}
              </div>
            </div>

            <p className="text-sm text-slate-600">
              Diteci chi siete prima di entrare.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              {campo("nome", "Nome", { obbligatorio: true, autoComplete: "given-name" })}
              {campo("cognome", "Cognome", { obbligatorio: true,
                                             autoComplete: "family-name" })}
            </div>

            <div>
              <Label>Ruolo<span className="text-orange-500"> *</span></Label>
              <select value={f.ruolo} data-testid="visita-ruolo"
                      onChange={(e) => setF({ ...f, ruolo: e.target.value })}
                      className="w-full h-11 mt-1 border border-slate-200 rounded-lg px-3 text-sm bg-white">
                <option value="">Scegli...</option>
                {(buono.ruoli_proposti || []).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            {campo("azienda", "Azienda o ente", { obbligatorio: true,
                                                  autoComplete: "organization" })}
            <div className="grid gap-4 sm:grid-cols-2">
              {campo("email", "E-mail", { autoComplete: "email" })}
              {campo("telefono", "Telefono", { autoComplete: "tel" })}
            </div>
            {campo("motivo", "Motivo della visita")}

            <Button onClick={entra} disabled={entro || manca}
                    data-testid="entra-visita"
                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white">
              {entro ? "Un attimo..." : "Entra"}
            </Button>
            <button type="button" onClick={() => setBuono(null)}
                    className="w-full text-xs text-slate-500 hover:text-slate-800 py-1 inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Cambia codice
            </button>
          </div>
        )}

        <p className="text-xs text-slate-400 text-center mt-4">
          La visita si chiude dopo {buono?.minuti_inattivita || 30} minuti senza
          attività. Per rientrare serve un codice nuovo.
        </p>
      </div>
    </div>
  );
}
